import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import ChipSelectorTsoPq, { ChipTsoPqProp } from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import { DefaultPageLayoutTsoPq } from '@components/Grid/GridTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { parseSubstationDevices, TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { findDiff, TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import {
  ApiControllersZipExportParameters,
  useLazyPostMeasurementGetZipExportQuery,
} from '@redux/apiSlices/measurementApi';
import {
  ApiCommonExternalDtoMeasurementDeviceExternalDto,
  useGetMeasurementDeviceGetAllQuery,
} from '@redux/apiSlices/measurementDeviceApi';
import {
  ApiCommonExternalDtoMeasurementParameterExternalDto,
  useGetMeasurementParameterGetAllQuery,
} from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useFlattenTreeProps } from '@utils/hooks/useFlattenTreeProp';
import { useLtreeTreeListFromObjects } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadInfo from './DownloadInfo';
import {
  StyledButtonContainer,
  StyledDeviceSelectionContainer,
  StyledLoadingSpinnerTsoPq,
  StyledMeasurmentExport,
  StyledParameters,
  StyledParametersContainer,
  StyledParametersLine,
  StyledSectionWrapper,
  StyledSpinnerBackground,
} from './MeasurmentExportStyles';
import { ApiCommonExternalDtoMeasurementLogExternalDto, usePostMeasurementLogGetSelectedMutation } from '@redux/apiSlices/measurementLogApi';

type AggregationIntervalProps = {
  id: number;
  label: string;
  value: string;
};

type Option = {
  id: number;
  value?: string;
  label: string;
};

type DeviceProps = { id: string; entityId: number; label: 'string'; technicalName: string; type: string };

const MeasurmentExport = () => {
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [selectedDevices, setSelectedDevices] = useState<{ [key: string]: string[] } | undefined>(undefined);
  const [activeTsos, setActiveTsos] = useState<TsosStateProps[] | [] | undefined>(undefined);
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});
  const [parameterOptions, setParameterOptions] = useState<TreeListItemTsoPqProps[] | undefined>([]);
  const [selectedParameters, setSelectedParameters] = useState<string[] | undefined>(undefined);
  const [activeTsoName, setActiveTsoName] = useState<string>('');
  const [aggregationIntervalsOptions, setAggregationIntervalsOptions] = useState<AggregationIntervalProps[]>([]);
  const [aggregationInterval, setAggregationInterval] = useState<Option | null | undefined>(undefined);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [checkedData, setCheckedData] = useState<boolean>(false);
  const [isCheckingForData, setIsCheckingForData] = useState<boolean>(false);
  const [measurementsAvailable, setMeasurementsAvailable] = useState<boolean[]>([]);
  const [activeDevices, setActiveDevices] = useState<number>(0);
  const [filteredParameters, setFilteredParameters] = useState<ApiCommonExternalDtoMeasurementParameterExternalDto[]>(
    []
  );
  const [mandatory, setMandatory] = useState<{
    activeTsos: boolean;
    startDate: boolean;
    endDate: boolean;
    aggregationInterval: boolean;
    selectedParameters: boolean;
    selectedDevices: {
      [key: string]: boolean;
    };
  }>({
    activeTsos: false,
    startDate: false,
    endDate: false,
    aggregationInterval: false,
    selectedParameters: false,
    selectedDevices: {},
  });

  const { t } = useTranslation();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();
  const { data: tsos, isSuccess } = useGetTsoGetAllQuery();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const [triggerZipExport, { isFetching }] = useLazyPostMeasurementGetZipExportQuery();
  const [triggerCheckDeviceData] = usePostMeasurementLogGetSelectedMutation();
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);
  const prevActiveDevicesRef = useRef<number>(activeDevices);
  const parameters = useLtreeTreeListFromObjects(filteredParameters);

  const handleStartDateChange = (date: dayjs.Dayjs) => {
    setStartDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    setMandatory((prev) => ({ ...prev, startDate: !date }));
  };

  const handleEndDateChange = (date: dayjs.Dayjs) => {
    setEndDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    setMandatory((prev) => ({ ...prev, endDate: !date }));
  };

  const handleDevicesChange = (id: number, devices: string[]) => {
    if (id && devices.length > 0) {
      setSelectedDevices((prev) => ({
        ...prev,
        [id]: devices,
      }));
    }
    if (id && devices.length === 0) {
      setSelectedDevices((prev) => ({
        ...prev,
        [id]: [],
      }));
    }
    const currentSelectedDevices: { [key: string]: string[] } = { ...selectedDevices, [id]: devices };

    setActiveDevices(
      Object.values(currentSelectedDevices).reduce((count, reducedDevice) => count + reducedDevice.length, 0)
    );

    if (devices.length > 0 || (devices.length === 0 && mandatory.selectedDevices[id] !== undefined))
      setMandatory((prev) => ({
        ...prev,
        selectedDevices: {
          ...prev.selectedDevices,
          [id]: devices.length <= 0,
        },
      }));
  };

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    const lastActiveTsoName = tsos.at(-1)?.name ?? '';

    setSelectedDevices((prev) => {
      const tsosWithSelectedDevices = prev ? Object.keys(prev) : [];
      const selectedTsosIds = tsos.map((tso) => `${tso.id}`);
      const unselectedTsos = tsosWithSelectedDevices.filter((tso) => !selectedTsosIds.includes(tso));

      const prevSelectedDevices = prev;

      prevSelectedDevices &&
        unselectedTsos?.map((tso) => {
          if (prevSelectedDevices[tso]) {
            delete prevSelectedDevices[tso];
          }
        });

      return prevSelectedDevices;
    });

    setActiveTsos(tsos);
    setActiveTsoName(lastActiveTsoName);
    const mandatorySelectedDevices = Object.assign(
      {},
      ...tsos.map((tso) => ({ [tso.id]: mandatory.selectedDevices[tso.id] }))
    );

    selectedDevices &&
      setActiveDevices(Object.values(selectedDevices).reduce((acc, devices) => acc + devices.length, 0));

    setMandatory((prev) => ({
      ...prev,
      activeTsos: formSubmitted ? !tsos.length : prev.activeTsos,
      selectedDevices: mandatorySelectedDevices,
    }));
  };

  const handleAggregationIntervalChange = (option: Option | null) => {
    setAggregationInterval(option);
    setMandatory((prev) => ({ ...prev, aggregationInterval: !option }));
  };

  const handleParametersChange = (selectedParameters: string[]) => {
    setSelectedParameters(selectedParameters);
    selectedParameters.length > 0 &&
      setMandatory((prev) => ({ ...prev, selectedParameters: !selectedParameters.length }));
  };

  const resetValues = async () => {
    await setActiveTsos(undefined);
    await setStartDate(undefined);
    await setEndDate(undefined);
    await setAggregationInterval(undefined);
    await setSelectedParameters(undefined);
    await setSelectedDevices(undefined);
    await setCheckedData(false);
    await setMandatory({
      activeTsos: false,
      startDate: false,
      endDate: false,
      aggregationInterval: false,
      selectedParameters: false,
      selectedDevices: {},
    });
  };

  const submitValues = async () => {
    setFormSubmitted(true);
    if (
      activeTsos &&
      startDate &&
      endDate &&
      aggregationInterval &&
      selectedParameters &&
      selectedParameters?.length > 0 &&
      selectedDevices
    ) {
      const usedParametersOptions =
        parameterOptions &&
        (useFlattenTreeProps(parameterOptions, ['id', 'entityId', 'label', 'type']) as {
          id: string;
          entityId: number;
          label: string;
          type: string;
        }[]);

      if (checkedIds) {
        const filteredusedParametersOptions = usedParametersOptions
          ?.filter((parameter) => parameter.type === 'item')
          .map((filteredParameter) => ({
            id: filteredParameter.entityId,
            label: filteredParameter.label,
            value: filteredParameter.id,
          }));

        const usedParameters = selectedParameters?.map((parameter) =>
          filteredusedParametersOptions?.find((option) => option.value === parameter)
        );

        const allDevices =
          measurementDevices?.externalDtos?.map((device) => ({
            technicalName: device.technicalName,
            id: device.id,
          })) ?? [];

        const technicalNames = allDevices
          .filter((device) => checkedIds.includes(Number(device.id)))
          .map((device) => device.technicalName);

        const zipExportFilters: ApiControllersZipExportParameters = {
          measurementDeviceTechnicalNames: technicalNames as string[],
          selection: usedParameters?.map((parameter) => parameter?.value) as string[],
        };

        try {
          for (const tso of activeTsos) {
            const response = await triggerZipExport({
              from: startDate,
              to: endDate,
              tsoId: tso.id,
              aggregationInterval: aggregationInterval?.value,
              apiControllersZipExportParameters: zipExportFilters,
            }).unwrap();
            const url = URL.createObjectURL(response as Blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `download_${tso.abbreviation}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    setMandatory({
      activeTsos: !activeTsos,
      startDate: !startDate,
      endDate: !endDate,
      aggregationInterval: !aggregationInterval,
      selectedParameters: !selectedParameters?.length,
      selectedDevices: selectedDevices
        ? Object.fromEntries(Object.entries(selectedDevices).map(([key, value]) => [key, value.length === 0]))
        : {},
    });
  };

  const checkForData = async () => {
    setFormSubmitted(true);
    setCheckedData(true);

    const allTsosHaveSelectedDevices = Object.values(mandatory.selectedDevices).every((value) => value === false);

    if (
      activeTsos &&
      startDate &&
      endDate &&
      aggregationInterval &&
      selectedParameters &&
      selectedParameters?.length > 0 &&
      selectedDevices &&
      allTsosHaveSelectedDevices
    ) {
      setCheckedIds([]);
      setMeasurementsAvailable([]);
      setIsCheckingForData(true);

      const deviceType = 2;
      const nodesProperties = Object.values(tsoDeviceOptions).flatMap(
        (entry) => entry && useFlattenTreeProps(entry, ['id', 'entityId', 'type'])
      ) as DeviceProps[];
      const deviceProperties = nodesProperties.filter((device) => Number(device?.type) === deviceType);
      const devicesById = Object.fromEntries(measurementDevices?.externalDtos.map(device => [device.id, device]));

      const hasDataChecksPerTso = getHasDataChecksPerTso(deviceProperties, devicesById);

      const results = await Promise.all(hasDataChecksPerTso);
      setMeasurementsAvailable(results.flatMap(result => result.deviceHasDataChecks));
      setCheckedIds(results.flatMap(result => result.deviceIdsFromLogs));

      setIsCheckingForData(false);
    } else {
      setMandatory({
        activeTsos: !activeTsos,
        startDate: !startDate,
        endDate: !endDate,
        aggregationInterval: !aggregationInterval,
        selectedParameters: !selectedParameters?.length,
        selectedDevices: selectedDevices
          ? Object.fromEntries(Object.entries(selectedDevices).map(([key, value]) => [key, value.length === 0]))
          : {},
      });
    }
  };

  const getHasDataChecksPerTso = (deviceProperties: DeviceProps[], devicesById: { [k: string]: ApiCommonExternalDtoMeasurementDeviceExternalDto; }) => {
    return Object.entries(selectedDevices).map(async ([tsoId, deviceIds]) => {
      const devicePropertiesByDeviceId = new Map(
        deviceProperties.map(deviceProperty => [deviceProperty.id, deviceProperty] as const)
      );

      const selectedDeviceIds = deviceIds
        .map(deviceId => devicePropertiesByDeviceId.get(deviceId))
        .filter(Boolean)
        .map(p => p.entityId);

      const { deviceHasDataChecks, deviceIdsFromLogs } = await getHasDataResults(startDate, endDate, tsoId, aggregationInterval, selectedDeviceIds, devicesById);

      return {
        deviceHasDataChecks,
        deviceIdsFromLogs
      };
    });
  }

  const getHasDataResults = async (
    startDate: string, endDate: string, tsoId: string, aggregationInterval: Option, selectedDeviceIds: number[], devicesById: { [k: string]: ApiCommonExternalDtoMeasurementDeviceExternalDto; }) => {

    const logsForDevicesResponse = await triggerCheckDeviceData({
      start: startDate,
      end: endDate,
      tsoId: +tsoId,
      aggregationIntervalId: aggregationInterval?.id,
      body: selectedDeviceIds.map(deviceId => devicesById[deviceId].technicalName),
    });

    const logsPerDeviceId = logsForDevicesResponse?.data?.externalDtos
      ?.reduce<Record<number, ApiCommonExternalDtoMeasurementLogExternalDto[]>>((logsPerDeviceId, log) => {
        if (!logsPerDeviceId[log.measurementDeviceId]) {
          logsPerDeviceId[log.measurementDeviceId] = [];
        }
        logsPerDeviceId[log.measurementDeviceId].push(log);
        return logsPerDeviceId;
      }, {});

    const deviceIdsFromLogs = Object.keys(logsPerDeviceId || {}).map(Number);
    const deviceHasDataChecks = selectedDeviceIds.map(selectedDeviceId => deviceIdsFromLogs.includes(selectedDeviceId));
    return { deviceHasDataChecks, deviceIdsFromLogs };
  }

  const hasMandatoryFieldsFilled = (object: { [key: string]: boolean | {} }) => {
    if (Object.keys(object).length > 0)
      return Object.values(object)
        .filter((value) => typeof value === 'boolean')
        .every((value) => value === false);
    return false;
  };

  const infoType = useMemo(() => {
    const changed = prevActiveDevicesRef.current !== activeDevices || prevActiveTsosRef.current !== activeTsos;
    const mandatoryFileds = hasMandatoryFieldsFilled(mandatory) && hasMandatoryFieldsFilled(mandatory.selectedDevices);

    if (!mandatoryFileds) {
      return 'noParameters';
    }

    if (changed && mandatoryFileds && checkedData && checkedIds) {
      return 'changedDevices';
    }

    if (checkedData && measurementsAvailable.every((deviceData) => deviceData === true)) {
      return 'dataFound';
    }

    if (checkedData && measurementsAvailable.every((deviceData) => deviceData === false)) {
      return 'noDataFound';
    }

    if (checkedData) {
      return 'partialDataFound';
    }

    return 'noParameters';
  }, [checkedData, checkedIds, activeDevices, activeTsos]);

  useEffect(() => {
    prevActiveDevicesRef.current = activeDevices;
  }, [activeDevices]);

  useEffect(() => {
    const unfilteredParameters = measurementParameters?.externalDtos;
    const intervals = aggregationIntervals?.externalDtos;
    const intervalId = intervals?.find((interval) => interval.technicalName === aggregationInterval?.value)?.id;
    const filteredParameters = unfilteredParameters?.filter(
      (unfilteredParameter) => unfilteredParameter.aggregationIntervalId === intervalId
    );
    setFilteredParameters(filteredParameters ?? []);
  }, [measurementParameters, aggregationInterval]);

  useEffect(() => {
    setParameterOptions(parameters);
  }, [filteredParameters]);

  useEffect(() => {
    const intervals = aggregationIntervals?.externalDtos;

    if (intervals) {
      const options =
        intervals
          .map((interval) => ({
            id: interval.id ?? NaN,
            label: t(`measurementExport.parameters.aggregationinterval.${interval.technicalName}`),
            value: interval.technicalName,
          }))
          .filter((item, index, self) => index === self.findIndex((i) => i.label === item.label)) ?? [];
      setAggregationIntervalsOptions(options);
    }
  }, [aggregationIntervals]);

  useEffect(() => {
    if (activeTsos) {
      const diff = findDiff({ activeTsos: activeTsos, prevActiveTsosRef: prevActiveTsosRef });

      const fetchDevices = async (id: number) => {
        if (isSuccess && tsos?.externalDtos?.length > 0) {
          try {
            const result = await triggerGetDeviceHierarchy(id, true);
            const parsedResult = result.data?.externalDtos && parseSubstationDevices(result.data?.externalDtos);
            parsedResult && setTsoDeviceOptions((prev) => ({ ...prev, [id]: parsedResult }));
          } catch (error) {
            console.error(error);
          }
        }
      };

      if (!Object.keys(tsoDeviceOptions).includes(`${diff[0]?.id}`)) {
        prevActiveTsosRef.current.length < activeTsos.length && fetchDevices(diff[0].id);
      }

      prevActiveTsosRef.current = activeTsos;
    }

    if (activeTsos && activeTsos.length === 0) {
      setCheckedData(false);
    }
  }, [activeTsos, activeTsoName]);
  return (
    <DefaultPageLayoutTsoPq>
      <StyledMeasurmentExport>
        <TypographyTsoPq typographyType="pageTitleBold">{t('measurementExport.page.title')}</TypographyTsoPq>
        <TypographyTsoPq typographyType="bodyTextLGBold">{t('measurementExport.page.subtitle')}</TypographyTsoPq>
        <StyledParametersContainer>
          <StyledParameters>
            <ChipSelectorTsoPq
              label={t('measurementExport.page.tso')}
              mandatory
              customWidth="33.5rem"
              activeChips={activeTsos ?? []}
              handleChange={handleActiveTsosChange}
              chips={tsos?.externalDtos as ChipTsoPqProp[]}
              disabled={false}
              multiple={true}
              error={mandatory.activeTsos}
              supportingText={mandatory.activeTsos ? t('general.error.valueSelect') : ''}
            ></ChipSelectorTsoPq>
            <StyledParametersLine>
              <DatepickerTsoPq
                label={t('measurementExport.parameters.start')}
                mandatory
                type={'datetimepicker'}
                width="34.5rem"
                enableFutureDates={false}
                date={startDate}
                handleChange={handleStartDateChange}
                error={mandatory.startDate}
                supportingText={mandatory.startDate ? t('general.error.dateSelect') : ''}
              />
              <DatepickerTsoPq
                label={t('measurementExport.parameters.end')}
                mandatory
                type={'datetimepicker'}
                width="34.5rem"
                enableFutureDates={false}
                date={endDate}
                handleChange={handleEndDateChange}
                minDateTime={dayjs(startDate)}
                error={mandatory.endDate}
                supportingText={mandatory.endDate ? t('general.error.dateSelect') : ''}
              />
            </StyledParametersLine>
            <StyledParametersLine>
              <SelectTsoPq
                label={t('measurementExport.parameters.aggregationInterval')}
                placeholder={t('measurementVisualization.parameters.aggregationInterval')}
                mandatory
                options={aggregationIntervalsOptions}
                customwidth="34.5rem"
                handleChange={handleAggregationIntervalChange}
                value={aggregationInterval ?? ({} as Option)}
                error={mandatory.aggregationInterval}
                supportingText={mandatory.aggregationInterval ? t('general.error.valueSelect') : ''}
              ></SelectTsoPq>
              <TreeAutocompleteTsoPq
                label={t('measurementExport.parameters.measurmentParameters')}
                mandatory
                disabled={!aggregationInterval?.id}
                customwidth="34.5rem"
                options={parameterOptions ?? []}
                placeholder={t('general.error.valueSelect')}
                handleChange={handleParametersChange}
                defaultValue={selectedParameters}
                error={mandatory.selectedParameters}
                supportingText={mandatory.selectedParameters ? t('general.error.valueSelect') : ''}
              ></TreeAutocompleteTsoPq>
            </StyledParametersLine>
          </StyledParameters>
          <StyledDeviceSelectionContainer>
            {activeTsos?.map((tso) => (
              <StyledSectionWrapper key={tso.id} selectDevice customWidth="33.625rem">
                <TypographyTsoPq typographyType="sectionTitleSMBold">{tso.name}</TypographyTsoPq>
                <TreeAutocompleteTsoPq
                  label={t('measurementExport.parameters.measurementDevice')}
                  customwidth="33.5rem"
                  options={tsoDeviceOptions[tso.id] ?? []}
                  placeholder={t('general.error.valueSelect')}
                  handleChange={(values) => handleDevicesChange(tso.id, values)}
                  defaultValue={selectedDevices?.[tso.id as keyof typeof selectedDevices]}
                  lowestLevel={3}
                  multiple={true}
                  mandatory
                  error={mandatory.selectedDevices[tso.id]}
                  supportingText={mandatory.selectedDevices[tso.id] ? t('general.error.valueSelect') : ''}
                />
              </StyledSectionWrapper>
            ))}
          </StyledDeviceSelectionContainer>
        </StyledParametersContainer>
        <StyledButtonContainer>
          <ButtonTsoPq
            buttonType={'outlinedPrimary'}
            textContent={t('measurementExport.parameters.resetAll')}
            onClick={() => {
              resetValues();
            }}
          />
          <ButtonTsoPq
            buttonType={'filledPrimary'}
            textContent={t('measurementExport.parameters.checkData')}
            onClick={() => {
              checkForData();
            }}
          />
        </StyledButtonContainer>
        {isCheckingForData || isFetching ? (
          <StyledSpinnerBackground>
            <StyledLoadingSpinnerTsoPq />
          </StyledSpinnerBackground>
        ) : (
          <DownloadInfo border={infoType !== 'noParameters'} type={infoType} submitValues={submitValues} />
        )}
      </StyledMeasurmentExport>
    </DefaultPageLayoutTsoPq>
  );
};

export default MeasurmentExport;

